import Divider from "components/divider";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { createLawImplementation, createLawImplementationProsecutorAssignment } from "services/lawImplementationService";
import { createLegalAwareness, createLegalAwarenessProsecutorAssignment, updateLegalAwareness } from "services/legalAwarenessService";
import { createLegalResearchRequest } from "services/legalResearchService";
import { createProsecutorAssignment } from "services/prosecutorAssignentService";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface TeamMember {
    id: number;
    name: string;
    role: string;
    isTeamLeader: boolean;
}

const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
    );
};

export default function LegalAwarenessCreationRequest({ request }: { request?: any }) {
    const { t } = useTranslation();
    const [selectedProsecutor, setSelectedProsecutor] =
        useState<TeamMember | null>(null);
    const [targetAudience, setTargetAudience] = useState(request?.target_audience || "");
    const [employees, setEmployees] = useState([]);
    const [task, setTask] = useState(request?.topic || "");
    const [remark, setRemark] = useState("");
    const { showToast } = useToast();
    const contextData = useAuth();
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>(request?.teams || []);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    
    const handleRemoveMember = (id: number) => {
        const memberToRemove = teamMembers.find((member) => member.id === id);
        if (
            memberToRemove?.isTeamLeader &&
            teamMembers.filter((m) => m.isTeamLeader).length === 1
        ) {
            setValidationError(
                t("CannotRemoveOnlyTeamLeader") || "Cannot remove the only team leader. Please assign a new team leader first or add another member."
            );
            return;
        }
        setTeamMembers(teamMembers.filter((member) => member.id !== id));
        setValidationError(null);
    };
    
    const handleToggleTeamLeader = (id: number) => {
        setTeamMembers((prevMembers) =>
            prevMembers.map((member) => {
                if (member.id === id) {
                    return { ...member, isTeamLeader: !member.isTeamLeader };
                } else {
                    return { ...member, isTeamLeader: false };
                }
            })
        );
        setValidationError(null);
    };
    
    const handleAddProsecutor = () => {
        if (
            selectedProsecutor &&
            !teamMembers.some((member) => member.id === selectedProsecutor.id)
        ) {
            const newId =
                teamMembers.length > 0
                    ? Math.max(...teamMembers.map((m) => m.id)) + 1
                    : 1;
            setTeamMembers((prevMembers) => [
                ...prevMembers,
                {
                    id: selectedProsecutor.id,
                    name: selectedProsecutor.name,
                    role: "Prosecutor",
                    isTeamLeader: prevMembers.length === 0,
                },
            ]);
            setSelectedProsecutor(null);
            setValidationError(null);
        } else if (
            teamMembers.some((member) => member.id === selectedProsecutor?.id)
        ) {
            showToast(t("ProsecutorAlreadyAdded") || "This prosecutor is already added to the team.", "warning");
        }
    };
    
    useEffect(() => {
        getEmployeesByAdminAndOrg({
            bearer:
                contextData.token ??
                contextData?.user?.access_token ??
                contextData?.user.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
        })
            .then((data) => {
                setEmployees(
                    data
                        .filter((emp: any) =>
                            emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
                        )
                        .map((emp: any) => ({
                            id: emp.user.id,
                            name: emp.user.first_name,
                            role: emp.roles[0] || "LegalResearchProsecutor",
                            isTeamLeader: false,
                        }))
                );
            })
            .catch((error) => {
                console.error("Error fetching:", error);
                showToast(t("FailedToLoadProsecutors") || "Failed to load prosecutors. Please refresh the page.", "error");
            });
    }, [getEmployeesByAdminAndOrg, setEmployees, contextData, showToast, t]);

    const handleSubmit = async () => {
        const currentTeamLeaders = teamMembers.filter(
            (member) => member.isTeamLeader
        );

        if (!task.trim()) {
            setValidationError(t("PleaseEnterTopic") || "Please enter a Topic.");
            return;
        }
        if (!targetAudience.trim()) {
            setValidationError(t("PleaseEnterTargetAudience") || "Please enter a Target Audience.");
            return;
        }
        if (teamMembers.length === 0) {
            setValidationError(t("PleaseAddAtLeastOneTeamMember") || "Please add at least one team member.");
            return;
        }
        if (currentTeamLeaders.length !== 1) {
            setValidationError(t("PleaseSelectExactlyOneTeamLeader") || "Please select exactly one team leader for the team.");
            return;
        }

        setValidationError(null);
        setIsSubmitting(true);
        showToast(t("SubmittingLegalAwarenessRequest") || "Submitting Legal Awareness Request...", "info");
        
        try {
            if (request) {
                // Update existing request
                const data = new FormData();
                data.append("topic", task);
                data.append("target_audience", targetAudience.trim());
                const response = await updateLegalAwareness(request.id, data);
                navigate(`/legal-awareness-creation/${request.id}`);
                showToast(t("LegalAwarenessRequestUpdatedSuccess") || "Legal Awareness Request updated successfully!", "success");
            } else {
                // Create new request
                const data = new FormData();
                data.append("topic", task);
                data.append("target_audience", targetAudience.trim());
                const response = await createLegalAwareness(data);

                for (const member of teamMembers) {
                    const prosecutorData = new FormData();
                    prosecutorData.append("awareness", response.id.toString());
                    prosecutorData.append("prosecutor_name", member.name);
                    prosecutorData.append("prosecutor_id", member.id.toString());
                    prosecutorData.append(
                        "role",
                        member.isTeamLeader ? "leader" : "member"
                    );
                    await createLegalAwarenessProsecutorAssignment(prosecutorData);
                }

                navigate(`/legal-awareness-creation/${response.id}`);
                showToast(t("LegalAwarenessRequestCreatedSuccess") || "Legal Awareness Request created successfully!", "success");
            }
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToCreateLegalAwarenessRequest") || "Failed to create Legal Awareness Request. Please try again.", "error");
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="rounded-lg bg-white w-[70%] p-8">
            <div className="flex items-center w-[50ch] gap-2">
                <h1 className="text-[#9D0303] text-lg font-semibold whitespace-nowrap flex-shrink-0">
                    {t("LegalAwarenessCreation") || "Legal Awareness Creation"}
                </h1>
                <hr className="bg-[#E9E9E9] h-0.5 w-full border-0" />
            </div>
            <form>
                {validationError && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {validationError}
                    </div>
                )}
                <div className="mb-6 mt-4">
                    <label
                        htmlFor="select-prosecutor"
                        className="block text-base font-medium text-gray-700 mb-2"
                    >
                        {t("AssignProsecutor") || "Assign Prosecutor"}
                    </label>
                    <div className="flex gap-2 mb-3">
                        <div className="relative flex-1">
                            <select
                                id="select-prosecutor"
                                className="appearance-none w-full p-3 border border-gray-300 rounded-md bg-[#F2F2F2] pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                                value={selectedProsecutor?.id || ""}
                                disabled={isSubmitting}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const found = employees.find(
                                        (employee: any) => String(employee.id) === selectedId
                                    );
                                    setSelectedProsecutor(found ?? null);
                                }}
                            >
                                <option value="">{t("SelectProsecutor") || "Select Prosecutor"}</option>
                                {employees.map((emp: any) => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name} - {emp.role}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleAddProsecutor}
                            className="bg-[#AA232F] hover:bg-[#8B1D2A] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                            disabled={isSubmitting}
                        >
                            {t("AddToTeam") || "Add to Team"}
                        </button>
                    </div>

                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
                        {teamMembers.length === 0 && (
                            <p className="text-gray-500 text-sm italic">
                                {t("NoTeamMembersAddedYet") || "No team members added yet. Please add at least one."}
                            </p>
                        )}
                        {teamMembers.map((member) => (
                            <div
                                key={member.id}
                                className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
                            >
                                <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                                    {getInitials(member.name)}
                                </div>

                                <div className="flex-grow">
                                    <p className="font-semibold text-gray-800">
                                        {member.name}
                                    </p>
                                    <p className="text-sm text-gray-500">{member.role}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id={`team-leader-${member.id}`}
                                        checked={member.isTeamLeader}
                                        onChange={() => handleToggleTeamLeader(member.id)}
                                        className="accent-[#AA232F] form-checkbox h-4 w-4 text-gray-800 border-gray-300 rounded"
                                        disabled={isSubmitting}
                                    />
                                    <label
                                        htmlFor={`team-leader-${member.id}`}
                                        className="text-sm text-gray-700"
                                    >
                                        {t("IsTeamLeader") || "is Team Leader"}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveMember(member.id)}
                                        className="text-gray-400 hover:text-red-500"
                                        disabled={isSubmitting}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="#AA232F"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                {t("ListOfTopic") || "List of Topic"}
                            </label>
                            <input
                                type="text"
                                value={task}
                                onChange={(e) => setTask(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md bg-[#F2F2F2] focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                                placeholder={t("EnterTopicPlaceholder") || "Enter topic"}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                {t("TargetAudience") || "Target Audience"}
                            </label>
                            <input
                                type="text"
                                value={targetAudience}
                                onChange={(e) => setTargetAudience(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md bg-[#F2F2F2] focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                                placeholder={t("EnterTargetAudiencePlaceholder") || "Enter target audience"}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="flex flex-grow mt-8 gap-4">
                        <button 
                            onClick={() => navigate(-1)} 
                            className="flex-1 border border-gray-800 py-4 rounded-lg" 
                            disabled={isSubmitting}
                        >
                            {t("Cancel") || "Cancel"}
                        </button>
                        <button 
                            type="button" 
                            onClick={handleSubmit} 
                            className="flex-1 bg-[#212121] text-white rounded-lg disabled:opacity-70" 
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (t("Submitting") || "Submitting...") : (t("Submit") || "Submit")}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}